import { useState, useMemo } from 'react';
import { ECSJob, ECSJobSortField, ECSJobSortConfig, FilterConfig } from '../types';
import { formatDate, getEnvColor } from '../utils';
import statusSuccessIcon from '../assets/icons/status-success.svg';
import statusFailedIcon from '../assets/icons/status-failed.svg';
import statusRunningIcon from '../assets/icons/status-running.svg';
import statusPendingIcon from '../assets/icons/status-pending.svg';
import statusUnknownIcon from '../assets/icons/status-unknown.svg';

const resultIcons: Record<string, string> = {
  success: statusSuccessIcon,
  failed: statusFailedIcon,
  running: statusRunningIcon,
  pending: statusPendingIcon,
  unknown: statusUnknownIcon,
};

// Normalize result status
const normalizeResult = (result: string | undefined): string => {
  if (!result) return 'unknown';
  const normalized = result.toLowerCase();
  if (['success', 'succeeded', 'completed'].includes(normalized)) return 'success';
  if (['failed', 'failure', 'error'].includes(normalized)) return 'failed';
  if (['running', 'in_progress', 'inprogress'].includes(normalized)) return 'running';
  if (['pending', 'queued', 'waiting'].includes(normalized)) return 'pending';
  return 'unknown';
};

interface ECSJobsTableProps {
  jobs: ECSJob[];
  loading: boolean;
  error: string | null;
  onRowClick?: (job: ECSJob) => void;
}

export function ECSJobsTable({
  jobs,
  loading,
  error,
  onRowClick,
}: ECSJobsTableProps) {
  const [sortConfig, setSortConfig] = useState<ECSJobSortConfig>({ field: 'LastRun', order: 'desc' });
  const [filters, setFilters] = useState<FilterConfig>({ env: '', app: '' });
  const [showDummy, setShowDummy] = useState(false);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    // Load favorites from cookie (separate from deployment favorites)
    try {
      const cookie = document.cookie
        .split('; ')
        .find((row) => row.startsWith('envappver_ecsjobs_favorites='));
      if (cookie) {
        const value = cookie.split('=')[1];
        return new Set(JSON.parse(decodeURIComponent(value)));
      }
    } catch {
      // Ignore parse errors
    }
    return new Set();
  });

  // Save favorites to cookie
  const saveFavorites = (favs: Set<string>) => {
    const expires = new Date();
    expires.setDate(expires.getDate() + 30);
    document.cookie = `envappver_ecsjobs_favorites=${encodeURIComponent(JSON.stringify([...favs]))};expires=${expires.toUTCString()};path=/`;
  };

  // Get unique environments for filter dropdown
  const environments = useMemo(() => {
    const envs = new Set(jobs.map((j) => j.Env));
    return Array.from(envs).sort();
  }, [jobs]);

  // Get unique applications for filter dropdown (filtered by showDummy)
  const applications = useMemo(() => {
    let filtered = jobs;
    if (!showDummy) {
      filtered = jobs.filter((j) => !j.App.toLowerCase().includes('dummy'));
    }
    const apps = new Set(filtered.map((j) => j.App));
    return Array.from(apps).sort();
  }, [jobs, showDummy]);

  // Filter and sort jobs
  const filteredJobs = useMemo(() => {
    let result = [...jobs];

    // Filter out Dummy apps unless showDummy is checked
    if (!showDummy) {
      result = result.filter((j) => !j.App.toLowerCase().includes('dummy'));
    }

    // Apply favorites filter
    if (showFavoritesOnly) {
      result = result.filter((j) => favorites.has(`${j.Env}#${j.App}`));
    }

    // Apply filters
    if (filters.env) {
      result = result.filter((j) => j.Env === filters.env);
    }
    if (filters.app) {
      result = result.filter((j) => j.App === filters.app);
    }

    // Apply sorting
    result.sort((a, b) => {
      const aVal = a[sortConfig.field] || '';
      const bVal = b[sortConfig.field] || '';

      if (aVal < bVal) return sortConfig.order === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.order === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [jobs, filters, sortConfig, showDummy, showFavoritesOnly, favorites]);

  const handleSort = (field: ECSJobSortField) => {
    setSortConfig((prev) => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIndicator = (field: ECSJobSortField) => {
    if (sortConfig.field !== field) return null;
    return sortConfig.order === 'asc' ? ' ▲' : ' ▼';
  };

  const toggleFavorite = (env: string, app: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    const key = `${env}#${app}`;
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      saveFavorites(next);
      return next;
    });
  };

  if (loading) {
    return <div className="loading">Loading ECS jobs...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="deployment-table-container">
      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="env-filter">Environment:</label>
          <select
            id="env-filter"
            value={filters.env}
            onChange={(e) => setFilters((f) => ({ ...f, env: e.target.value }))}
          >
            <option value="">All Environments</option>
            {environments.map((env) => (
              <option key={env} value={env}>
                {env}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="app-filter">Application:</label>
          <select
            id="app-filter"
            value={filters.app}
            onChange={(e) => setFilters((f) => ({ ...f, app: e.target.value }))}
          >
            <option value="">All Applications</option>
            {applications.map((app) => (
              <option key={app} value={app}>
                {app}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group checkbox-group">
          <label htmlFor="show-favorites-only">
            <input
              id="show-favorites-only"
              type="checkbox"
              checked={showFavoritesOnly}
              onChange={(e) => setShowFavoritesOnly(e.target.checked)}
            />
            Favorites Only
          </label>
        </div>
        <div className="filter-group checkbox-group">
          <label htmlFor="show-dummy">
            <input
              id="show-dummy"
              type="checkbox"
              checked={showDummy}
              onChange={(e) => setShowDummy(e.target.checked)}
            />
            Show Dummy
          </label>
        </div>
        <div className="filter-info">
          Showing {filteredJobs.length} of {jobs.length} jobs
        </div>
      </div>

      {/* Table */}
      <table className="deployment-table">
        <thead>
          <tr>
            <th className="favorite-col"></th>
            <th onClick={() => handleSort('Account')} className="sortable">
              Account{getSortIndicator('Account')}
            </th>
            <th onClick={() => handleSort('Env')} className="sortable">
              Environment{getSortIndicator('Env')}
            </th>
            <th onClick={() => handleSort('App')} className="sortable">
              Application{getSortIndicator('App')}
            </th>
            <th onClick={() => handleSort('Ver')} className="sortable">
              Version{getSortIndicator('Ver')}
            </th>
            <th onClick={() => handleSort('LastRun')} className="sortable">
              Last Run{getSortIndicator('LastRun')}
            </th>
            <th onClick={() => handleSort('Result')} className="sortable">
              Result{getSortIndicator('Result')}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredJobs.length === 0 ? (
            <tr>
              <td colSpan={7} className="no-data">
                No ECS jobs found
              </td>
            </tr>
          ) : (
            filteredJobs.map((job, index) => {
              const isFavorite = favorites.has(`${job.Env}#${job.App}`);
              const normalizedResult = normalizeResult(job.Result);
              return (
                <tr
                  key={`${job.Env}-${job.App}-${index}`}
                  className={`env-${job.Env.toLowerCase()}`}
                  style={{ backgroundColor: getEnvColor(job.Env) }}
                  onClick={() => onRowClick?.(job)}
                >
                  <td
                    className="favorite-cell"
                    onClick={(e) => toggleFavorite(job.Env, job.App, e)}
                    style={{ cursor: 'pointer', color: isFavorite ? '#FFD700' : '#ccc' }}
                  >
                    {isFavorite ? '★' : '☆'}
                  </td>
                  <td className="account-cell">{job.Account || ''}</td>
                  <td className="env-cell">{job.Env}</td>
                  <td>{job.App}</td>
                  <td className="version-cell">{job.Ver}</td>
                  <td>{formatDate(job.LastRun)}</td>
                  <td className={`status-cell status-${normalizedResult}`}>
                    <img
                      src={resultIcons[normalizedResult]}
                      alt={job.Result || 'Unknown'}
                      title={job.Result || 'Unknown'}
                      className="status-icon"
                    />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
